/*
 * 戒导航 · 提及模块（jiedh/mention）—— 自研 Tiptap 节点 mention
 * =============================================================================
 * 设计依据：packages/mention/extend.php 头部注释「一、要解决的问题 / 三、做法」
 *
 * 【为什么必须自研这个节点】
 *   flarum/mentions 产出的提及是**标记语法**（帖子提及 @"显示名"#p34、用户提及
 *   @"显示名"#12 或 @用户名、群组提及 @"组名"#g3、标签提及 #slug），
 *   而 fof/rich-text 的富文本编辑器（Tiptap）不认识这些写法：
 *   TiptapEditorDriver.insertBetween() 里 escape=false 时走 `parser.parse(text)`，
 *   markdown-it 没有对应规则 ⇒ 整串降级成纯文本 ⇒ 输入框里直接显示提及源码原文。
 *   本节点把提及变成「内联原子节点」，从而：
 *     · 编辑器内渲染成与帖子正文一致的提及小标签（不再是源码原文）；
 *     · 序列化时**原样还原**提及源码（落库格式与改造前逐字节一致，extend.php 第四节红线）。
 *
 * 【硬约束一：必须复用 fof/rich-text 的 Tiptap / ProseMirror 实例】
 *   绝对不能在本包内 `import { Node } from '@tiptap/core'` —— 那会打出第二份 @tiptap/core
 *   与第二份 @tiptap/pm/model，导致 schema 校验失败等极难排查的问题。
 *   正确做法：运行期从 flarum.reg 取回 fof/rich-text 已经注册、并被重新导出的 primitives，
 *   它与编辑器内部使用的是同一个模块实例，见：
 *     vendor/fof/rich-text/js/src/common/tiptap/tiptap.ts
 *     vendor/fof/rich-text/js/dist/common/tiptap/TiptapEditorDriver.js 里
 *       flarum.reg.add("fof-rich-text","common/tiptap/tiptap",{Node:…,Mark:…,Extension:…})
 *
 * 【硬约束二：属性名必须与 mentionSyntax.ts 的 MentionAttrs 一致】
 *   buildTokens 的 getAttrs 返回的就是这份属性对象，键名对不上会导致 addNode 用
 *   createAndFill 建节点时属性丢失（prosemirror 的 createAndFill 会静默丢弃不合规节点）。
 *
 * 【为什么 renderHTML 输出 <span> 而不是服务端那样的 <a href>】
 *   服务端渲染 UserMention / PostMention / TagMention 时带 <a href>（可点击跳转）。
 *   编辑器内的 DOM 若也带 <a href>，编辑正文时误点提及会直接跳转离开编辑器。
 *   因此编辑器内统一渲染成 <span>，但**类名与内部结构与服务端保持一致**
 *   （.UserMention / .PostMention / .GroupMention / .TagMention、
 *     .GroupMention-name / .TagMention-text），从而完整复用 vendor/flarum/mentions/less/forum.less
 *   的既有样式，本模块无需注入任何 CSS。
 *
 * 【标签提及只显示 #slug，不查 store 取标签名 / 颜色】
 *   保持与用户实际键入的源码形态一致（HashMentionFormat.format(slug) → `#slug`），
 *   且避免给 renderHTML 引入对 app.store 的运行时依赖（store 未加载时无法降级）。
 */
import { MENTION_NODE } from './mentionSyntax';

/** fof/rich-text 重新导出 Tiptap primitives 时的注册坐标。 */
const RICH_TEXT_NAMESPACE = 'fof-rich-text';
const RICH_TEXT_TIPTAP_ID = 'common/tiptap/tiptap';

/**
 * 从注册表取回 fof/rich-text 的 Tiptap primitives。
 *
 * 为什么是运行期取、而不是顶层 import：
 *   TiptapEditorDriver 及其依赖被打在 fof/rich-text 的异步 chunk 里（见
 *   vendor/fof/rich-text/js/src/forum/applyEditor.tsx 里的动态 import），
 *   只有在编辑器真正构建时才保证已注册。本函数只在 buildExtensions 回调里被调用，
 *   那时模块必然已就绪。
 *
 * @returns { Node, Mark, Extension, … } | null —— 取不到时返回 null（调用方降级跳过，绝不让编辑器崩）。
 */
function getTiptapPrimitives() {
  const primitives = flarum.reg.get(RICH_TEXT_NAMESPACE, RICH_TEXT_TIPTAP_ID);
  if (!primitives || !primitives.Node || typeof primitives.Node.create !== 'function') {
    console.warn('[jiedh/mention] 未能从 flarum.reg 取到 fof-rich-text 的 Tiptap primitives' + `（${RICH_TEXT_NAMESPACE} / ${RICH_TEXT_TIPTAP_ID}），` + '提及的富文本支持已跳过，编辑器将退回「显示提及源码原文」的旧行为。');
    return null;
  }
  return primitives;
}

/**
 * 编辑器内的 DOM 结构（按提及种类分派）。
 *
 * 结构对齐服务端 XSL 渲染结果，便于直接复用 flarum/mentions 的样式：
 *   · UserMention   →  @显示名
 *   · PostMention   →  ↩ 显示名（服务端用 <i class="icon fas fa-reply"></i>）
 *   · GroupMention  →  @组名（服务端包一层 .GroupMention-name）
 *   · TagMention    →  #slug（服务端包一层 .TagMention-text）
 *
 * 注：`@"名"#ID` 这种 safe 格式用户提及与旧式 `@用户名` 在服务端渲染完全相同，
 * 因此 UserMention 与 user-legacy 走同一分支。
 */
function buildMentionDom(attrs) {
  switch (attrs.kind) {
    case 'post':
      return ['span',
      // data-id 与服务端 <a class="PostMention" data-id="…"> 对齐，方便调试 / 未来定位
      {
        class: 'PostMention',
        'data-id': attrs.id ?? ''
      }, ['i', {
        class: 'icon fas fa-reply'
      }], attrs.displayName ?? ''];
    case 'group':
      return ['span', {
        class: 'GroupMention'
      }, ['span', {
        class: 'GroupMention-name'
      }, `@${attrs.displayName ?? ''}`]];
    case 'tag':
      return ['span', {
        class: 'TagMention'
      }, ['span', {
        class: 'TagMention-text'
      }, `#${attrs.slug ?? ''}`]];
    default:
      // 'user' / 'user-legacy'
      return ['span', {
        class: 'UserMention'
      }, `@${attrs.displayName ?? ''}`];
  }
}

/**
 * 创建 mention 内联原子节点。
 * 每次编辑器构建都会调用一次（ItemList 是新建的），因此这里每次都新建节点实例。
 */
export function createMentionNode() {
  const primitives = getTiptapPrimitives();
  if (!primitives) return null;
  const {
    Node
  } = primitives;
  return Node.create({
    name: MENTION_NODE,
    /* ---- schema 定义 ---------------------------------------------------- */
    // 内联 + 原子：整段提及在正文里作为一个不可拆分的整体，光标只能落在它两侧。
    inline: true,
    group: 'inline',
    atom: true,
    /* ---- 属性（键名必须与 mentionSyntax.ts 的 MentionAttrs 完全一致）---- */
    addAttributes() {
      return {
        /** 提及种类（post / user / group / tag / user-legacy），决定渲染成哪一种小标签 */
        kind: {
          default: 'user'
        },
        /** 显示名 / 用户名 */
        displayName: {
          default: null
        },
        /** 数字 ID（帖子 / 用户 / 群组） */
        id: {
          default: null
        },
        /** 标签 slug */
        slug: {
          default: null
        },
        /** 原始源码切片，序列化时优先原样回放（保证落库逐字节不变） */
        raw: {
          default: ''
        }
      };
    },
    /*
     * 不做 HTML 解析。
     * 提及源码只从 Markdown 侧进来（初始内容 / 编辑老帖走 parser.parse，
     * 回复按钮走 insertAtCursor(…, false) 的 markdown 解析），
     * 不通过粘贴 HTML 进入编辑器，因此无需 parseHTML 规则。
     * （若返回空数组，ProseMirror 的 parseDOM 里就没有本节点，粘贴渲染过的 HTML 不会误建节点。）
     */
    parseHTML() {
      return [];
    },
    /* ---- HTML 渲染（编辑器内 DOM）-------------------------------------- */
    renderHTML(_ref) {
      let {
        node
      } = _ref;
      return buildMentionDom(node.attrs);
    }
  });
}